import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, X, Plus, ExternalLink, Award, ArrowLeft, Check, Upload, Phone, KeyRound, Wallet } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { Spinner } from "../components/common";
import { useAuth } from "../context/AuthContext";

/* Browseable catalog of ALL loyalty programs — airlines, hotels, car rental,
   cruise, rail, credit cards, membership + supermarkets — extracted from the
   TripIt picker. Visitors search, filter by category, then tap "Add" to save
   the program to their private wallet (existing POST /api/loyalty/records).
   Supports URL deep-links (?cat=shopping&q=nike), an Add-with-credentials
   modal (phone-hint for supermarkets/other), and a CSV bulk import. */

const CAT_COLOR = {
  airline:     "bg-sky-50 text-sky-700 border-sky-200",
  hotel:       "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  car:         "bg-emerald-50 text-emerald-700 border-emerald-200",
  cruise:      "bg-blue-50 text-blue-700 border-blue-200",
  rail:        "bg-orange-50 text-orange-700 border-orange-200",
  card:        "bg-violet-50 text-violet-700 border-violet-200",
  membership:  "bg-amber-50 text-amber-700 border-amber-200",
  supermarket: "bg-lime-50 text-lime-700 border-lime-200",
  department:  "bg-rose-50 text-rose-700 border-rose-200",
  shopping:    "bg-pink-50 text-pink-700 border-pink-200",
  other:       "bg-slate-50 text-slate-700 border-slate-200",
};

// Categories where the member-id is usually a phone number.
const PHONE_ID_CATS = new Set(["supermarket", "other"]);

export default function LoyaltyCatalog() {
  const { user } = useAuth() || {};
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const cat = params.get("cat") || "";
  const [categories, setCategories] = useState([]);
  const [data, setData] = useState(null);
  const [added, setAdded] = useState({}); // id -> { recordId, loginUser }
  const [addOpen, setAddOpen] = useState(null); // program object being added
  const [importBusy, setImportBusy] = useState(false);
  const [guestPrompts, setGuestPrompts] = useState(() => {
    if (typeof window === "undefined") return 0;
    return Number(localStorage.getItem("loyalty_guest_prompts") || 0);
  });
  const [nudgeDismissed, setNudgeDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem("loyalty_guest_nudge_dismissed") === "1";
  });
  const fileRef = useRef(null);

  const bumpGuestPrompt = () => {
    const next = guestPrompts + 1;
    setGuestPrompts(next);
    try { localStorage.setItem("loyalty_guest_prompts", String(next)); } catch { /* ignore quota */ }
  };
  const dismissNudge = () => {
    setNudgeDismissed(true);
    try { localStorage.setItem("loyalty_guest_nudge_dismissed", "1"); } catch { /* ignore */ }
  };

  const setQ = (v) => {
    const next = new URLSearchParams(params);
    if (v) next.set("q", v); else next.delete("q");
    setParams(next, { replace: true });
  };
  const setCat = (v) => {
    const next = new URLSearchParams(params);
    if (v) next.set("cat", v); else next.delete("cat");
    setParams(next, { replace: true });
  };

  useEffect(() => {
    api.get("/loyalty-catalog/categories")
      .then((r) => setCategories(r.data?.categories || []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    setData(null);
    api.get("/loyalty-catalog/programs", { params: { category: cat, q } })
      .then((r) => setData(r.data))
      .catch(() => setData({ items: [], letters: [], by_letter: {} }));
  }, [cat, q]);

  const totals = useMemo(() => {
    return categories.reduce((sum, c) => sum + (c.count || 0), 0);
  }, [categories]);

  const openAdd = (p) => {
    if (!user) {
      bumpGuestPrompt();
      toast.error("Sign in to add loyalty programs to your wallet.");
      return;
    }
    setAddOpen(p);
  };

  const submitAdd = async (p, extras) => {
    try {
      const payload = {
        airline_name: p.name,
        program_name: p.program,
        ...extras,
      };
      const { data: r } = await api.post("/loyalty/records", payload);
      setAdded((v) => ({ ...v, [p.id]: { recordId: r.id, loginUser: extras.login_user || "" } }));
      toast.success(`${p.display} added to your programs.`);
      setAddOpen(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add — please try again.");
    }
  };

  const checkBalance = async (p) => {
    const info = added[p.id];
    const user = info?.loginUser;
    if (user && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(user);
        toast.success("Username copied — paste it on the login page.");
      } catch (e) {
        toast.message(`Your username: ${user}`);
      }
    }
    window.open(p.official_link, "_blank", "noopener,noreferrer");
  };

  const onCsvChosen = async (file) => {
    if (!file) return;
    if (!user) {
      bumpGuestPrompt();
      toast.error("Sign in to import your loyalty programs.");
      return;
    }
    setImportBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: r } = await api.post("/loyalty/records/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const parts = [];
      if (r.created) parts.push(`${r.created} added`);
      if (r.updated) parts.push(`${r.updated} updated`);
      if (r.skipped) parts.push(`${r.skipped} skipped`);
      toast.success(`CSV imported — ${parts.join(", ") || "no changes"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CSV import failed.");
    } finally {
      setImportBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="loyalty-catalog">
      {/* Hero */}
      <section className="bg-gradient-to-br from-indigo-900 via-blue-800 to-blue-600 text-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-12">
          <Link to="/loyalty" data-testid="catalog-back"
            className="inline-flex items-center gap-1 text-blue-200 text-sm hover:text-white mb-3">
            <ArrowLeft className="w-4 h-4" /> Back to loyalty overview
          </Link>
          <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-amber-300 mb-3 flex items-center gap-2">
            <Award className="w-4 h-4" /> Mileage & Loyalty Programs
          </div>
          <h1 className="font-serif font-800 text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
            Every Airline Mileage and other Loyalty programs worth tracking in one place
          </h1>
          <p className="text-blue-100 mt-4 max-w-2xl text-lg">
            Browse {totals || "hundreds of"}+ airline, hotel, car rental, cruise, rail, credit-card, membership, supermarket, department-store and shopping-site rewards programs.
            Tap Add to save one to your private wallet — sign in to sync across devices.
          </p>

          <form onSubmit={(e) => e.preventDefault()} className="mt-6 flex items-center gap-2 bg-white/95 rounded-full pl-5 pr-1 py-1 max-w-xl shadow-lg">
            <Search className="w-4 h-4 text-slate-400 shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="catalog-search"
              placeholder="Search programs — airline, hotel, supermarket..."
              className="flex-1 bg-transparent outline-none text-slate-800 placeholder:text-slate-400 py-2" />
            {q && <button type="button" onClick={() => setQ("")} data-testid="catalog-clear"
              className="w-8 h-8 rounded-full grid place-items-center text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>}
          </form>

          <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
            <input ref={fileRef} type="file" accept=".csv,text/csv" hidden
              data-testid="catalog-csv-file"
              onChange={(e) => onCsvChosen(e.target.files?.[0])} />
            <button type="button"
              data-testid="catalog-import-csv"
              disabled={importBusy}
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 px-3.5 py-1.5 text-blue-50 disabled:opacity-60">
              <Upload className="w-4 h-4" />
              {importBusy ? "Importing…" : "Import balances CSV"}
            </button>
            <a href="data:text/csv;charset=utf-8,airline_name,program_name,member_number,balance,tier,expiry_date,login_user,login_password,notes%0AAmerican,AAdvantage,ABC12345,52340,Gold,2026-12-31,,,%0AKroger,Plus / Fuel Points,+15035550100,8210,,,,,Phone-based ID"
              download="loyalty-import-template.csv"
              data-testid="catalog-csv-template"
              className="text-blue-200 hover:text-white underline underline-offset-2">
              Download CSV template
            </a>
          </div>
        </div>
      </section>

      {/* Category chips */}
      <div className="sticky top-16 z-20 bg-slate-50/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-3 flex gap-2 overflow-x-auto scrollbar-none" style={{ scrollbarWidth: "none" }}>
          <button onClick={() => setCat("")} data-testid="catalog-cat-all"
            className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-700 border transition ${cat === "" ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-400"}`}>
            All {totals ? `(${totals})` : ""}
          </button>
          {categories.map((c) => (
            <button key={c.key} onClick={() => setCat(c.key)} data-testid={`catalog-cat-${c.key}`}
              className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-700 border transition ${cat === c.key ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-blue-400"}`}>
              {c.emoji} {c.label} {c.count ? `(${c.count})` : ""}
            </button>
          ))}
        </div>
      </div>

      {/* Guest sync nudge — appears once a guest has been prompted to sign in 3+ times */}
      {!user && guestPrompts >= 3 && !nudgeDismissed && (
        <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-5" data-testid="catalog-sync-nudge">
          <div className="rounded-2xl border border-amber-300 bg-gradient-to-br from-amber-50 to-white p-4 flex items-start gap-4 shadow-sm">
            <div className="w-11 h-11 rounded-xl bg-amber-500 text-white grid place-items-center text-xl shrink-0 shadow">☁️</div>
            <div className="min-w-0 flex-1">
              <div className="font-serif font-800 text-lg text-amber-900 leading-tight">
                Sign in to save your programs and sync across devices
              </div>
              <div className="text-sm text-amber-800/85 mt-0.5">
                You've tried to add {guestPrompts} program{guestPrompts === 1 ? "" : "s"} — creating a free account keeps them safe on your phone, laptop and tablet.
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Link to="/signup" data-testid="catalog-sync-signup"
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-sm font-800 px-4 py-2 transition">
                  Create a free account
                </Link>
                <Link to="/login" data-testid="catalog-sync-signin"
                  className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-amber-50 border border-amber-300 text-amber-800 text-sm font-700 px-4 py-2 transition">
                  Sign in
                </Link>
              </div>
            </div>
            <button onClick={dismissNudge} data-testid="catalog-sync-dismiss"
              aria-label="Dismiss"
              className="w-7 h-7 rounded-full grid place-items-center text-amber-500 hover:bg-amber-100 shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* A-Z listing */}
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8">
        {!data ? <Spinner /> : (
          data.items?.length === 0 ? (
            <div className="text-center py-16 text-slate-500">
              <div className="text-lg font-700 text-slate-700">No matches</div>
              <div className="text-sm mt-1">Try clearing filters or searching a different name.</div>
            </div>
          ) : (
            <div className="space-y-8">
              {data.letters.map((letter) => (
                <section key={letter} data-testid={`catalog-letter-${letter}`}>
                  <div className="sticky top-32 z-10 -mx-5 lg:-mx-8 px-5 lg:px-8 py-2 bg-slate-50/95 backdrop-blur">
                    <div className="text-[11px] font-mono uppercase tracking-[0.32em] text-slate-500">{letter}</div>
                  </div>
                  <ul className="mt-2 grid md:grid-cols-2 gap-2.5">
                    {data.by_letter[letter].map((p) => {
                      const isAdded = !!added[p.id];
                      const catInfo = categories.find((c) => c.key === p.category);
                      return (
                        <li key={p.id} data-testid={`catalog-program-${p.id}`}
                          className="flex items-center gap-3 bg-white rounded-2xl border border-slate-200 p-3.5 hover:shadow-sm transition-shadow">
                          <span className={`w-11 h-11 rounded-xl border grid place-items-center text-lg shrink-0 ${CAT_COLOR[p.category] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
                            {catInfo?.emoji || "•"}
                          </span>
                          <div className="min-w-0 flex-1">
                            <div className="font-serif font-800 text-base text-blue-900 leading-tight truncate">{p.name}</div>
                            <div className="text-xs text-slate-500 truncate">{p.program} · <span className="uppercase tracking-wider text-[10px]">{catInfo?.label || p.category}</span></div>
                          </div>
                          <a href={p.official_link} target="_blank" rel="noopener noreferrer"
                            data-testid={`catalog-open-${p.id}`}
                            className="w-8 h-8 rounded-full grid place-items-center text-slate-400 hover:text-blue-600 shrink-0"
                            aria-label="Open official page">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                          {isAdded ? (
                            <button onClick={() => checkBalance(p)}
                              data-testid={`catalog-check-${p.id}`}
                              title="Copy username and open the login page"
                              className="shrink-0 inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 px-3 py-1.5 text-xs font-700 transition">
                              <Wallet className="w-3.5 h-3.5" /> Check balance
                            </button>
                          ) : (
                            <button onClick={() => openAdd(p)}
                              data-testid={`catalog-add-${p.id}`}
                              className="shrink-0 inline-flex items-center gap-1 rounded-full px-3.5 py-1.5 text-xs font-700 transition bg-blue-600 text-white hover:brightness-110">
                              <Plus className="w-3.5 h-3.5" /> Add
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          )
        )}
      </div>

      {addOpen && (
        <AddCredentialsModal
          program={addOpen}
          onClose={() => setAddOpen(null)}
          onSubmit={(extras) => submitAdd(addOpen, extras)}
        />
      )}
    </div>
  );
}

function AddCredentialsModal({ program, onClose, onSubmit }) {
  const isPhone = PHONE_ID_CATS.has(program.category);
  const [memberNumber, setMemberNumber] = useState("");
  const [balance, setBalance] = useState("");
  const [tier, setTier] = useState("");
  const [loginUser, setLoginUser] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (withCreds = true) => {
    setBusy(true);
    const extras = withCreds ? {
      member_number: memberNumber || undefined,
      balance: balance || undefined,
      tier: tier || undefined,
      login_user: loginUser || undefined,
      login_password: loginPassword || undefined,
      notes: notes || undefined,
    } : {};
    try {
      await onSubmit(extras);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="catalog-add-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden"
      >
        <div className="px-5 pt-5 pb-3 border-b border-slate-100 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-slate-500">Add to wallet</div>
            <div className="font-serif font-800 text-xl text-blue-900 leading-tight mt-0.5 truncate">{program.name}</div>
            <div className="text-xs text-slate-500">{program.program}</div>
          </div>
          <button onClick={onClose} data-testid="catalog-add-close"
            className="w-8 h-8 rounded-full grid place-items-center text-slate-400 hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-3.5 text-sm">
          <Field
            label={isPhone ? "Phone number (used as your ID)" : "Member number"}
            icon={isPhone ? Phone : KeyRound}
            testid="add-member-number"
            placeholder={isPhone ? "e.g. +1 503 555 0100" : "e.g. ABC12345"}
            value={memberNumber}
            onChange={setMemberNumber}
            inputMode={isPhone ? "tel" : "text"}
          />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Balance" testid="add-balance" placeholder="0"
              value={balance} onChange={setBalance} inputMode="numeric" />
            <Field label="Tier / status" testid="add-tier" placeholder="Gold"
              value={tier} onChange={setTier} />
          </div>

          <details className="rounded-xl bg-slate-50 border border-slate-200">
            <summary className="px-3 py-2 text-xs font-700 text-slate-600 cursor-pointer select-none flex items-center gap-2">
              <KeyRound className="w-3.5 h-3.5" />
              Optional: save login (encrypted, requires re-auth to reveal)
            </summary>
            <div className="p-3 pt-1 space-y-3">
              <Field label="Login username / email" testid="add-login-user"
                value={loginUser} onChange={setLoginUser} placeholder="you@example.com" />
              <Field label="Login password" testid="add-login-password" type="password"
                value={loginPassword} onChange={setLoginPassword} placeholder="••••••" />
              <Field label="Notes" testid="add-notes"
                value={notes} onChange={setNotes} placeholder="Anything else?" />
            </div>
          </details>

          <div className="text-[11px] text-slate-500 leading-relaxed">
            {isPhone
              ? "Most supermarket programs use your phone number to look you up at checkout."
              : "Your member number stays private in your wallet — we mask it in the UI."}
          </div>
        </div>

        <div className="px-5 pb-5 pt-1 flex items-center justify-between gap-2">
          <button onClick={() => submit(false)} disabled={busy}
            data-testid="catalog-add-skip"
            className="text-xs font-700 text-slate-500 hover:text-slate-700 underline underline-offset-2">
            Skip — just save the program
          </button>
          <button onClick={() => submit(true)} disabled={busy}
            data-testid="catalog-add-save"
            className="inline-flex items-center gap-1.5 rounded-full bg-blue-600 hover:brightness-110 text-white text-sm font-700 px-5 py-2 disabled:opacity-60">
            {busy ? "Saving…" : (<><Plus className="w-4 h-4" /> Add to wallet</>)}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, testid, type = "text", inputMode, icon: Icon }) {
  return (
    <label className="block">
      <div className="text-[11px] font-700 uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      <div className="relative">
        {Icon && <Icon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />}
        <input
          data-testid={testid}
          type={type}
          inputMode={inputMode}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full rounded-xl border border-slate-200 bg-white text-slate-800 text-sm py-2 ${Icon ? "pl-9" : "pl-3"} pr-3 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100`}
        />
      </div>
    </label>
  );
}
